import { useEffect, useMemo, useState } from 'react';
import { db } from '@/offline/db';
import { cn } from '@/lib/cn';
import {
  Copy, Printer, FileText, AlertTriangle, RefreshCw,
  Users, ClipboardList, X, Check,
} from 'lucide-react';
import type { EPCR, VitalSet, Procedure, MedicationGiven } from '@/types';
import {
  buildTOCScript, buildCode99TOCScript, buildRefusalTOCScript,
  type TOCBullet,
} from '@/lib/toc-script-builder';

/**
 * TransferOfCareScript — addendum § 10 visual component.
 *
 * Full-screen readable handoff script. Six numbered bullets in MIST+
 * format (medical / trauma variant), or Code 99 / Refusal variants
 * when those apply. Auto-regenerates when the ePCR data changes.
 *
 * Typography is deliberately oversized (24pt body by default, 32pt
 * headers) so the crew can read it aloud without squinting at the
 * iPad under ED lighting.
 *
 * Missing data shows as amber '[not documented]' placeholders — a
 * visible prompt to fill in upstream. This is the feedback loop that
 * encourages complete documentation during the call.
 */

interface Props {
  epcr: EPCR;
  onClose?: () => void;
  /** Which variant to show. Defaults to 'standard'. */
  variant?: 'standard' | 'code99' | 'refusal' | 'auto';
}

export function TransferOfCareScript({ epcr, onClose, variant = 'auto' }: Props) {
  const [vitals, setVitals] = useState<VitalSet[]>([]);
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [medications, setMedications] = useState<MedicationGiven[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [reportGiven, setReportGiven] = useState(false);
  const [receivingName, setReceivingName] = useState('');
  const [receivingRole, setReceivingRole] = useState('Nurse');

  useEffect(() => {
    let alive = true;
    void (async () => {
      const [v, p, m] = await Promise.all([
        db.vital_sets.where('epcr_id').equals(epcr.id).toArray(),
        db.procedures.where('epcr_id').equals(epcr.id).toArray(),
        db.medications_given.where('epcr_id').equals(epcr.id).toArray(),
      ]);
      if (!alive) return;
      setVitals(v);
      setProcedures(p);
      setMedications(m);
    })();
    return () => { alive = false; };
  }, [epcr.id, refreshKey]);

  // Resolve variant
  const resolved: 'standard' | 'code99' | 'refusal' = useMemo(() => {
    if (variant !== 'auto') return variant;
    if (epcr.transport_decision === 'refusal_care' || epcr.transport_decision === 'refusal_transport') return 'refusal';
    const isArrest = /cardiac arrest|pulseless|rosc/i.test(
      `${epcr.primary_impression ?? ''} ${epcr.chief_complaint ?? ''}`,
    );
    if (isArrest) return 'code99';
    return 'standard';
  }, [variant, epcr]);

  const bullets: TOCBullet[] = useMemo(() => {
    const ctx = { epcr, vitals, procedures, medications };
    if (resolved === 'code99') {
      // Pull Code 99 extras from the session record
      return buildCode99TOCScript(ctx);
    }
    if (resolved === 'refusal') return buildRefusalTOCScript(ctx);
    return buildTOCScript(ctx);
  }, [resolved, epcr, vitals, procedures, medications]);

  const gapsCount = bullets.filter((b) => b.hasGaps).length;

  function plainText(): string {
    return bullets.map((b) => `${b.n}. ${b.category}: ${b.text}`).join('\n');
  }

  async function copy() {
    try { await navigator.clipboard?.writeText(plainText()); } catch { /* ignore */ }
  }

  function printScript() {
    const html = `<html><head><title>Transfer of Care · ${epcr.id.slice(0, 8)}</title>
      <style>body{font-family:system-ui;font-size:14pt;padding:24px}h1{font-size:18pt}ol{padding-left:24pt}li{margin-bottom:8pt}</style>
    </head><body>
      <h1>Transfer of Care · ${epcr.patient_last_name ?? 'Patient'}</h1>
      <ol>${bullets.map((b) => `<li><strong>${b.category}:</strong> ${b.text}</li>`).join('')}</ol>
      <p style="margin-top:18pt;font-size:10pt;color:#555">Generated ${new Date().toLocaleString()} · EMSPCR</p>
    </body></html>`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.print();
  }

  async function markReportGiven() {
    const now = new Date().toISOString();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await db.epcrs.update(epcr.id, {
      toc_completed: true,
      toc_completed_at: now,
      toc_receiving_facility: epcr.toc_receiving_facility ?? epcr.destination_hospital,
      toc_delivery_method: 'verbal_report',
      _dirty: true,
      _last_modified: now,
    } as any);
    setReportGiven(true);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="w-full max-w-3xl max-h-[95vh] flex flex-col border border-slate-700 bg-slate-950 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-700 bg-slate-900 px-4 py-2">
          <div className="flex items-center gap-2">
            <ClipboardList size={16} className="text-blue-400" />
            <span className="text-sm font-bold uppercase tracking-widest text-slate-100">
              Transfer of Care ·
            </span>
            <span className={cn(
              'status-pill',
              resolved === 'code99'   ? 'bg-red-950 text-red-300 border border-red-800' :
              resolved === 'refusal'  ? 'bg-amber-950 text-amber-300 border border-amber-800' :
              'bg-blue-950 text-blue-300 border border-blue-800',
            )}>
              {resolved === 'code99' ? 'Code 99 Variant' : resolved === 'refusal' ? 'Refusal Variant' : 'MIST+'}
            </span>
            {gapsCount > 0 && (
              <span className="status-pill bg-amber-950 text-amber-300 border border-amber-800 inline-flex items-center gap-1">
                <AlertTriangle size={10} /> {gapsCount} gap{gapsCount === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setRefreshKey((k) => k + 1)} className="p-1.5 text-slate-500 hover:text-slate-200" title="Refresh">
              <RefreshCw size={14} />
            </button>
            {onClose && (
              <button onClick={onClose} className="p-1.5 text-slate-500 hover:text-slate-200" title="Close">
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Bullets — read-aloud typography */}
        <div className="flex-1 overflow-y-auto p-4">
          <ol className="space-y-4">
            {bullets.map((b) => (
              <li key={b.n} className="flex items-start gap-3">
                <span className="flex-shrink-0 flex items-center justify-center h-9 w-9 bg-blue-600 text-white font-bold text-lg tabular-nums">
                  {b.n}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-0.5">
                    {b.category}
                  </div>
                  <div className={cn(
                    'text-lg leading-snug',
                    b.hasGaps ? 'text-amber-200' : 'text-slate-100',
                  )}>
                    {b.text}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Footer actions */}
        <div className="border-t border-slate-700 bg-slate-900 p-3 space-y-2">
          {reportGiven ? (
            <div className="flex items-center gap-2 text-emerald-300 text-sm">
              <Check size={16} />
              Report given to {receivingRole} {receivingName || '(name not entered)'} · TOC timestamped.
            </div>
          ) : (
            <div className="grid gap-2 md:grid-cols-[1fr_140px_140px]">
              <input
                type="text"
                value={receivingName}
                onChange={(e) => setReceivingName(e.target.value)}
                placeholder="Receiving clinician name"
                className="input-field"
              />
              <select
                value={receivingRole}
                onChange={(e) => setReceivingRole(e.target.value)}
                className="input-field"
              >
                <option>Nurse</option>
                <option>Charge Nurse</option>
                <option>Physician</option>
                <option>Resident</option>
                <option>PA</option>
                <option>NP</option>
                <option>Receiving Clinician</option>
              </select>
              <button
                onClick={markReportGiven}
                disabled={!receivingName.trim()}
                className="btn-primary gap-1 text-sm"
              >
                <Users size={13} />
                Report Given
              </button>
            </div>
          )}
          <div className="flex flex-wrap gap-2 justify-end">
            <button onClick={copy} className="btn-secondary text-xs gap-1">
              <Copy size={12} /> Copy
            </button>
            <button onClick={printScript} className="btn-secondary text-xs gap-1">
              <Printer size={12} /> Print
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Keep FileText imported for future variants
void FileText;
